import { getEnvironment } from '@minecraft.environment';

export const stripLeadingSlash = (p: string): string => (p.startsWith('/') ? p.slice(1) : p);
export const stripTrailingSlash = (p: string): string => (p.endsWith('/') && p.length > 1 ? p.slice(0, -1) : p);

export const joinPath = (parent: string, child: string): string => {
  if (!parent) return child;
  if (!child) return parent;
  if (parent === '/') return child.startsWith('/') ? child : `/${child}`;
  return `${stripTrailingSlash(parent)}/${stripLeadingSlash(child)}`;
};

const EXTERNAL_PROTOCOLS = ['http', 'https', 'mailto', 'tel', 'sms'];

const AUDIENCE_TO_URL_MAP = ({ environment }: { environment: ReturnType<typeof getEnvironment> }) => ({
  admin: environment.ADMIN_URL,
  casting: environment.CASTING_URL,
  reps: environment.REPS_URL,
  talent: environment.TALENT_URL,
  'public-pages': environment.PUBLIC_URL,
  login: environment.ULA_URL,
});

const getAudienceBaseUrl = (): string => {
  const environment = getEnvironment();
  const audienceUrl = AUDIENCE_TO_URL_MAP({ environment })[environment.AUDIENCE];

  if (!audienceUrl) {
    // fallback to window.location.origin if AUDIENCE does not match any of the keys in AUDIENCE_TO_URL_MAP
    // this can potentially happen in onboarding and bau-login potentially, but they are not in use and should be removed soonish
    return `${window.location.origin}${environment.BASE_NAME}`;
  }

  return audienceUrl;
};

export const isExternalUrl = (url: string): boolean => EXTERNAL_PROTOCOLS.includes(url.split(':')[0]);

export const buildAudiencePrefixedUrl = (path: string): string => {
  const audienceBaseUrl = getAudienceBaseUrl();
  if (path.startsWith('/')) return `${audienceBaseUrl}${path}`;
  if (path.startsWith('?') || path.startsWith('#')) return `${window.location.pathname}${path}`;
  const currentParts = window.location.pathname.split('/');
  currentParts.pop();
  return `${currentParts.join('/')}/${path}`;
};

type SearchParamValue = string | string[] | Record<string, string>;

/**
 * Parses a URL search string into a structured object with the following rules:
 * - A key that appears once → string value
 * - A key that appears multiple times → string[]
 * - bracket[] notation  (e.g. `tag[]=a&tag[]=b`)  → string[] under key `tag`
 * - keyed bracket notation (e.g. `filter[type]=shoe`) → nested Record under key `filter`
 */
export const parseSearchParams = (search: string): Record<string, SearchParamValue> => {
  const params = new URLSearchParams(search);
  const seen = new Set<string>();
  const result: Record<string, SearchParamValue> = {};

  const keys = Array.from(params.keys());
  keys.forEach((key) => {
    if (seen.has(key)) return;
    seen.add(key);

    const values = params.getAll(key);

    // bracket[] notation → array under key without []
    // e.g. bracket[]=red&bracket[]=green → { bracket: ['red', 'green'] }
    if (key.endsWith('[]')) {
      result[key.slice(0, -2)] = values;
      return;
    }

    // keyed bracket notation → nested object
    // e.g. filter[type]=shoe&filter[size]=10 → { filter: { type: 'shoe', size: '10' } }
    const objMatch = key.match(/^([^[]+)\[([^\]]+)\]$/);
    if (objMatch) {
      const [, objKey, propKey] = objMatch;
      const existing = result[objKey];
      if (!existing || typeof existing !== 'object' || Array.isArray(existing)) {
        result[objKey] = {};
      }
      const value = values?.[0] ?? '';
      (result[objKey] as Record<string, string>)[propKey] = value;
      return;
    }

    // plain key → string for single value, array for multiple values
    result[key] = values.length === 1 ? values[0] : values;
  });

  return result;
};
